"use client";

import type { MarginsRow } from "@/lib/types/financial-snapshot";
import { pctLabel, niceScale, CHART_PADDING, CHART_HEIGHT, YAxis, XAxisLabels, ChartSvg } from "../ChartAxes";
import { useChartHover } from "../hover/use-chart-hover";
import { HoverHitAreas } from "../hover/HoverHitAreas";
import { HoverCrosshair } from "../hover/HoverCrosshair";
import { ChartTooltip } from "../hover/ChartTooltip";
import type { HoverPoint } from "../hover/types";

const VIEW_WIDTH = 640;

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;

export function MarginsChart({ rows }: { rows: MarginsRow[] }) {
  const { containerRef, activeIndex, handlePointerMove, handlePointerLeave, handlePointerUp } = useChartHover();

  if (rows.length < 2) return null;

  const allVals = rows.flatMap((r) => [r.gross, r.operating, r.net].filter((v): v is number => v != null));
  if (allVals.length === 0) return null;
  const maxVal = Math.max(...allVals);
  const minVal = Math.min(...allVals, 0);
  const scale = niceScale(maxVal, minVal);

  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const yScale = (v: number) => plotBottom - ((v - scale.min) / (scale.max - scale.min)) * plotHeight;
  const xScale = (i: number) =>
    CHART_PADDING.left + (i / (rows.length - 1)) * (VIEW_WIDTH - CHART_PADDING.left - CHART_PADDING.right);

  const anchors = rows.map((_, i) => xScale(i));

  const points: HoverPoint[] = rows.map((row) => ({
    period: row.period,
    series: [
      { label: "Gross", value: row.gross, format: fmtPct, colorClass: "bg-blue" },
      { label: "Operating", value: row.operating, format: fmtPct, colorClass: "bg-cgreen" },
      { label: "Net", value: row.net, format: fmtPct, colorClass: "bg-amber-400" },
    ],
  }));

  function polyline(key: "gross" | "operating" | "net"): string {
    return rows
      .map((r, i) => (r[key] != null ? `${xScale(i)},${yScale(r[key] as number)}` : null))
      .filter((p): p is string => p !== null)
      .join(" ");
  }

  return (
    <div ref={containerRef} className="relative">
      <ChartSvg viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}>
        <YAxis ticks={scale.ticks} scale={yScale} format={pctLabel} width={VIEW_WIDTH} />
        <polyline fill="none" className="stroke-blue" strokeWidth={2} points={polyline("gross")} />
        <polyline fill="none" className="stroke-cgreen" strokeWidth={2} points={polyline("operating")} />
        <polyline fill="none" className="stroke-amber-400" strokeWidth={2} points={polyline("net")} />
        <XAxisLabels labels={rows.map((r) => r.period)} xScale={xScale} y={CHART_HEIGHT - 8} />

        <g transform={`translate(${CHART_PADDING.left}, 4)`}>
          <rect width={8} height={8} className="fill-blue" />
          <text x={12} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>Gross</text>
          <rect x={60} width={8} height={8} className="fill-cgreen" />
          <text x={72} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>Operating</text>
          <rect x={140} width={8} height={8} className="fill-amber-400" />
          <text x={152} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>Net</text>
        </g>

        {activeIndex != null && <HoverCrosshair x={anchors[activeIndex]} plotTop={plotTop} plotBottom={plotBottom} />}
        <HoverHitAreas
          anchors={anchors}
          viewBoxWidth={VIEW_WIDTH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          onActivate={handlePointerMove}
          onLeave={handlePointerLeave}
          onTap={handlePointerUp}
        />
      </ChartSvg>
      {activeIndex != null && (
        <ChartTooltip point={points[activeIndex]} anchorPct={(anchors[activeIndex] / VIEW_WIDTH) * 100} />
      )}
    </div>
  );
}
